"use client";

import { useState } from "react";
import Link from "next/link";
import { useQuery } from "convex/react";
import { makeFunctionReference } from "convex/server";
import {
  AlertTriangle,
  ChevronRight,
  ExternalLink,
  LayoutDashboard,
  Plus,
  Sparkles,
  Truck,
} from "lucide-react";
import { LoadAgentPanel } from "@/components/loads/LoadDetailClient";
import { TenantStateGate } from "@/components/tenant/TenantContextProvider";
import { Badge } from "@/components/ui/badge";
import { QuerySkeleton } from "@/components/ui/skeleton";

/**
 * Orchestrator Console — the "talk to the Haulbase Agent" surface.
 *
 * This is the real thing: it lists the tenant's live loads via loads:listBoard,
 * starts the multi-agent workflow through agents/public:startLoadAgentWorkflow
 * for the selected load, and streams run status / approval packets / decisions
 * from agents/public:listWorkflowRunsForLoad. Every outbound action the agent
 * drafts is held for approval in /inbox. No scripted scenarios, no demo loads.
 */

type BoardRow = {
  id: string;
  reference: string;
  customerName: string;
  carrierName: string | null;
  route: string;
  lifecycleState: string;
  agentState: string;
  label: string;
  riskScore: number | null;
};

const listBoard = makeFunctionReference<
  "query",
  { tenantId: string; limit?: number },
  BoardRow[]
>("loads:listBoard");

export function OrchestratorConsole() {
  // Fail closed: without a configured Convex backend there is no agent to run.
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return (
      <ConsoleChrome>
        <div className="rounded-lg border border-border px-4 py-10 text-center text-sm text-muted-foreground">
          <AlertTriangle size={16} className="mx-auto mb-2" />
          The agent console is unavailable — the Convex backend is not configured for this
          environment.
        </div>
      </ConsoleChrome>
    );
  }

  return (
    <TenantStateGate>
      {(tenant) => <LiveOrchestratorConsole tenantId={tenant.tenantId} />}
    </TenantStateGate>
  );
}

function LiveOrchestratorConsole({ tenantId }: { tenantId: string }) {
  const rows = useQuery(listBoard, { tenantId, limit: 100 });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (rows === undefined) {
    return (
      <ConsoleChrome>
        <QuerySkeleton surface="consoleDashboard" />
      </ConsoleChrome>
    );
  }

  if (rows.length === 0) {
    return (
      <ConsoleChrome>
        <div className="rounded-lg border border-border px-4 py-12 text-center">
          <p className="text-sm font-medium text-foreground">No loads yet</p>
          <p className="mt-1 text-[13px] text-muted-foreground">
            The agent works real loads — create one and it will show up here, ready to run.
          </p>
          <Link
            href="/loads/new"
            className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
          >
            <Plus size={13} />
            Create a load
          </Link>
        </div>
      </ConsoleChrome>
    );
  }

  const selected = rows.find((row) => row.id === selectedId) ?? rows[0];

  return (
    <ConsoleChrome reference={selected.reference}>
      <div className="grid gap-4 lg:grid-cols-[1.45fr_1fr]">
        <div>
          <div className="mb-3 flex items-center gap-2">
            <label htmlFor="agent-load-picker" className="text-xs text-muted-foreground">
              Load
            </label>
            <select
              id="agent-load-picker"
              value={selected.id}
              onChange={(event) => setSelectedId(event.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1.5 text-[13px] outline-none focus:ring-2 focus:ring-ring"
            >
              {rows.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.reference} — {row.route}
                </option>
              ))}
            </select>
            <Link
              href={`/loads/${selected.id}`}
              className="inline-flex items-center gap-1 text-[12px] text-primary hover:underline"
            >
              Open load <ExternalLink size={12} />
            </Link>
          </div>

          <LoadAgentPanel
            tenantId={tenantId}
            loadId={selected.id}
            loadReference={selected.reference}
          />
        </div>

        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-card p-3.5">
            <div className="mb-1 text-xs text-muted-foreground">Details</div>
            <Detail k="Reference" v={selected.reference} />
            <Detail k="Route" v={selected.route} />
            <Detail k="Customer" v={selected.customerName} />
            <Detail k="Carrier" v={selected.carrierName ?? "Unassigned"} muted={!selected.carrierName} />
            <Detail k="Lifecycle" v={selected.lifecycleState} />
            <Detail
              k="Risk"
              v={selected.riskScore === null ? "—" : String(selected.riskScore)}
              muted={selected.riskScore === null}
            />
          </div>

          <div className="rounded-lg border border-border bg-card p-3.5">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Agent state</span>
              <Badge variant={selected.label === "Decision needed" ? "warn" : "neutral"}>
                {selected.label}
              </Badge>
            </div>
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">
              Runs execute the real multi-agent workflow: supervisor planning, carrier vetting,
              pricing, tracking, documents, billing, and customer comms. Outbound actions are
              drafted and held for approval in{" "}
              <Link href="/inbox" className="text-primary hover:underline">
                the inbox
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </ConsoleChrome>
  );
}

function ConsoleChrome({
  reference,
  children,
}: {
  reference?: string;
  children: React.ReactNode;
}) {
  return (
    <main className="mx-auto max-w-6xl px-5 py-6">
      <div className="overflow-hidden rounded-lg border border-border bg-background">
        <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2.5">
          <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <Link href="/" className="flex items-center gap-1.5 transition-colors hover:text-foreground">
              <LayoutDashboard size={15} />
              <span>Console</span>
            </Link>
            <ChevronRight size={13} />
            <Truck size={15} />
            <Link href="/loads" className="transition-colors hover:text-foreground">
              Loads
            </Link>
            {reference && (
              <>
                <ChevronRight size={13} />
                <span className="text-foreground">{reference}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-primary/10 text-primary">
              <Sparkles size={13} />
            </span>
            <span className="text-sm font-medium text-foreground">Haulbase agent</span>
          </div>
        </div>
        <div className="px-4 py-4">{children}</div>
      </div>
    </main>
  );
}

function Detail({ k, v, muted }: { k: string; v: string; muted?: boolean }) {
  return (
    <div className="flex justify-between gap-3 border-t border-border py-1.5 text-[13px] first:border-t-0">
      <span className="text-muted-foreground">{k}</span>
      <span className={muted ? "text-muted-foreground" : undefined}>{v}</span>
    </div>
  );
}
